/**
 * Admin Ebook Editor
 * Create/Edit ebooks with file upload support
 */
import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '../../config';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { adminApiGet, adminApiPost, adminApiPatch } from '../../utils/adminApi';
import toast from '../../utils/toast';

import {
  ArrowLeft,
  CircleNotch,
  Eye,
  FileText,
  FloppyDisk,
  Image as ImageIcon,
  UploadSimple
} from '@phosphor-icons/react';
const EbookEditorNew = () => {
  const navigate = useNavigate();
  const { ebookId } = useParams();
  const isEdit = !!ebookId;

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    file_path: '',
    file_url: '',
    cover_image: '',
    pages: '',
    format: 'PDF',
    published: false,
  });

  useEffect(() => {
    if (isEdit) {
      fetchEbook();
    }
  }, [ebookId]);

  const fetchEbook = async () => {
    try {
      setLoading(true);
      const data = await adminApiGet('/api/admin/ebooks');
      const ebook = data.find(e => e.id === ebookId);
      if (ebook) {
        setFormData({
          title: ebook.title || '',
          description: ebook.description || '',
          category: ebook.category || '',
          file_path: ebook.file_path || '',
          file_url: ebook.file_url || '',
          cover_image: ebook.cover_image || '',
          pages: ebook.pages || '',
          format: ebook.format || 'PDF',
          published: ebook.published || false,
        });
      } else {
        toast.error('Ebook not found');
        navigate('/admin/ebooks');
      }
    } catch (error) {
      console.error('Error fetching ebook:', error);
      toast.error('Failed to load ebook');
      navigate('/admin/ebooks');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      toast.error('Please upload a PDF file');
      return;
    }

    try {
      setUploadingFile(true);
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);

      // Use fetch for file upload (adminApi doesn't handle FormData well)
      const token = localStorage.getItem('admin_token');
      
      const response = await fetch(`${BACKEND_URL}/api/admin/upload-ebook`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: uploadFormData,
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      const data = await response.json();
      setFormData(prev => ({
        ...prev,
        file_path: data.file_path || data.path,
        file_url: data.file_url || data.url,
      }));
      toast.success('PDF uploaded successfully');
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('Failed to upload PDF');
    } finally {
      setUploadingFile(false);
    }
  };

  const handleCoverUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    try {
      setUploadingCover(true);
      const uploadFormData = new FormData();
      uploadFormData.append('image', file);
      const token = localStorage.getItem('admin_token');
      
      const response = await fetch(`${BACKEND_URL}/api/admin/upload-image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: uploadFormData,
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      const data = await response.json();
      setFormData(prev => ({
        ...prev,
        cover_image: data.file_url || data.url,
      }));
      toast.success('Cover image uploaded successfully');
    } catch (error) {
      console.error('Error uploading cover:', error);
      toast.error('Failed to upload cover image');
    } finally {
      setUploadingCover(false);
    }
  };

  const handleSave = async (publish = false) => {
    if (!formData.title.trim()) {
      toast.error('Title is required');
      return;
    }

    if (!formData.file_path && !formData.file_url) {
      toast.error('Please upload a PDF file or provide a file URL');
      return;
    }

    try {
      setSaving(true);
      const payload = {
        ...formData,
        published: publish ? true : formData.published,
      };

      if (isEdit) {
        await adminApiPatch(`/api/admin/ebooks/${ebookId}`, payload);
        toast.success('Ebook updated successfully');
      } else {
        await adminApiPost('/api/admin/ebooks', payload);
        toast.success('Ebook created successfully');
      }

      navigate('/admin/ebooks');
    } catch (error) {
      console.error('Error saving ebook:', error);
      toast.error(error.message || 'Failed to save ebook');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-12 bg-white/10 rounded animate-pulse w-64"></div>
        <div className="h-96 bg-white/10 rounded-xl animate-pulse"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/admin/ebooks')}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-white">{isEdit ? 'Edit Ebook' : 'New Ebook'}</h1>
            <p className="text-gray-400">
              {isEdit ? 'Update ebook details' : 'Add a new downloadable ebook'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => handleSave(false)}
            disabled={saving}
            className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {saving ? <CircleNotch size={18} className="animate-spin" /> : <FloppyDisk size={18} />}
            Save Draft
          </button>
          <button
            onClick={() => handleSave(true)}
            disabled={saving}
            className="px-4 py-2 bg-gradient-to-r from-[#6366F1] to-[#EC4899] rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center gap-2 font-semibold"
          >
            {saving ? <CircleNotch size={18} className="animate-spin" /> : <Eye size={18} />}
            Publish
          </button>
        </div>
      </div>

      {/* Form */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Title */}
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
            <label className="block text-sm font-medium text-gray-300 mb-2">Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              placeholder="Enter ebook title..."
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#6366F1]/50"
            />
          </div>

          {/* Description */}
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
            <label className="block text-sm font-medium text-gray-300 mb-2">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Brief description of the ebook..."
              rows={6}
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#6366F1]/50 resize-none"
            />
          </div>

          {/* PDF UploadSimple */}
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
            <label className="block text-sm font-medium text-gray-300 mb-2">PDF File *</label>
            
            {formData.file_path || formData.file_url ? (
              <div className="mb-4 p-4 bg-green-500/10 border border-green-500/30 rounded-lg flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <FileText size={24} className="text-green-400" />
                  <div>
                    <p className="text-white font-medium">PDF Uploaded</p>
                    <p className="text-xs text-gray-400">{formData.file_path || formData.file_url}</p>
                  </div>
                </div>
                <button
                  onClick={() => handleChange('file_path', '')}
                  className="text-red-400 hover:text-red-300 text-sm"
                >
                  Remove
                </button>
              </div>
            ) : null}

            <div className="space-y-4">
              <div>
                <label className="block w-full cursor-pointer">
                  <div className="border-2 border-dashed border-white/20 rounded-lg p-8 text-center hover:border-[#6366F1]/50 transition-colors">
                    {uploadingFile ? (
                      <div className="flex flex-col items-center gap-2">
                        <CircleNotch size={32} className="animate-spin text-[#6366F1]" />
                        <p className="text-sm text-gray-400">Uploading PDF...</p>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-2">
                        <UploadSimple size={32} className="text-gray-400" />
                        <p className="text-white font-medium">Click to upload PDF</p>
                        <p className="text-sm text-gray-400">or drag and drop</p>
                      </div>
                    )}
                  </div>
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={handleFileUpload}
                    className="hidden"
                    disabled={uploadingFile}
                  />
                </label>
              </div>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-white/10"></div>
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-[#1A1B1E] px-2 text-gray-400">Or provide URL</span>
                </div>
              </div>

              <input
                type="text"
                value={formData.file_url}
                onChange={(e) => handleChange('file_url', e.target.value)}
                placeholder="https://example.com/ebook.pdf"
                className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#6366F1]/50"
              />
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Category & Meta */}
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Details</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Category</label>
                <input
                  type="text"
                  value={formData.category}
                  onChange={(e) => handleChange('category', e.target.value)}
                  placeholder="AI & Technology"
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#6366F1]/50"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Pages</label>
                <input
                  type="number"
                  value={formData.pages}
                  onChange={(e) => handleChange('pages', e.target.value)}
                  placeholder="120"
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#6366F1]/50"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Format</label>
                <input
                  type="text"
                  value={formData.format}
                  onChange={(e) => handleChange('format', e.target.value)}
                  placeholder="PDF"
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#6366F1]/50"
                />
              </div>
            </div>
          </div>

          {/* Cover Image */}
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Cover Image</h3>
            
            {formData.cover_image && (
              <img
                src={formData.cover_image}
                alt="Cover preview"
                className="w-full h-48 object-cover rounded-lg mb-4"
                onError={(e) => e.target.style.display = 'none'}
              />
            )}

            <label className="block w-full cursor-pointer">
              <div className="border-2 border-dashed border-white/20 rounded-lg p-6 text-center hover:border-[#6366F1]/50 transition-colors">
                {uploadingCover ? (
                  <div className="flex flex-col items-center gap-2">
                    <CircleNotch size={24} className="animate-spin text-[#6366F1]" />
                    <p className="text-xs text-gray-400">Uploading...</p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-2">
                    <ImageIcon size={24} className="text-gray-400" />
                    <p className="text-sm text-white">UploadSimple Cover</p>
                  </div>
                )}
              </div>
              <input
                type="file"
                accept="image/*"
                onChange={handleCoverUpload}
                className="hidden"
                disabled={uploadingCover}
              />
            </label>
            
            <input
              type="text"
              value={formData.cover_image}
              onChange={(e) => handleChange('cover_image', e.target.value)}
              placeholder="Or paste image URL"
              className="w-full mt-3 px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#6366F1]/50"
            />
          </div>

          {/* Status */}
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Status</h3>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-300">Published</span>
              <button
                onClick={() => handleChange('published', !formData.published)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  formData.published ? 'bg-[#6366F1]' : 'bg-white/20'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    formData.published ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EbookEditorNew;
